import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Autocomplete,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import Sidebar from './Sidebar';
import Header from './Header';
import './UserManagement.css';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from './ConfirmationModal';

interface User {
  id: number;
  google_id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  profile_picture?: string;
  created_at: string;
  updated_at: string;
  last_login?: string;
  is_active: boolean;
  email_verified: boolean;
  user_type: string;
  user_role: string;
  organization_id?: number;
  organization_name?: string;
}

interface Organization {
  id: number;
  name: string;
  slug: string;
}

interface Institution {
  id: number;
  name: string;
  slug: string;
  organization_id: number;
}

interface UserFormData {
  first_name: string;
  last_name: string;
  email: string;
  user_role: string;
  organization_id: number | null;
  institution_ids: number[];
  is_active: boolean;
}

const UserManagement: React.FC = () => {
  const { isGlobalAdmin, isOrgAdmin, isInstitutionAdmin, organization, user: currentUser, impersonateUser } = useAuth();
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [filters, setFilters] = useState({
    organizationId: '',
    institutionId: '',
    userRole: '',
    search: '',
    status: 'active'
  });
  const [formData, setFormData] = useState<UserFormData>({
    first_name: '',
    last_name: '',
    email: '',
    user_role: 'teacher',
    organization_id: null,
    institution_ids: [],
    is_active: true
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null as User | null });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchUsers();
    if (isGlobalAdmin) {
      fetchOrganizations();
    }
    if (isGlobalAdmin || isOrgAdmin) {
      fetchInstitutions();
    }
  }, [filters]);

  // Clear institution selections when organization changes
  useEffect(() => {
    if (formData.organization_id) {
      // Clear any institution selections that don't belong to the selected organization
      const validInstitutionIds = institutions
        .filter(inst => inst.organization_id === formData.organization_id)
        .map(inst => inst.id);
      
      const filteredInstitutionIds = formData.institution_ids.filter(id => 
        validInstitutionIds.includes(id)
      );
      
      if (filteredInstitutionIds.length !== formData.institution_ids.length) {
        setFormData(prev => ({ ...prev, institution_ids: filteredInstitutionIds }));
      }
    }
  }, [formData.organization_id, institutions]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      
      if (filters.organizationId) {
        queryParams.append('organization_id', filters.organizationId);
      }
      if (filters.institutionId) {
        queryParams.append('institution_id', filters.institutionId);
      }
      if (filters.userRole) {
        queryParams.append('user_role', filters.userRole);
      }
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/admin/users?${queryParams.toString()}`);
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Failed to fetch users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/organizations`);
      setOrganizations(response.data.organizations || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const fetchInstitutions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/institutions`);
      setInstitutions(response.data.institutions || []);
    } catch (error) {
      console.error('Error fetching institutions:', error);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDialog = async (user?: User) => {
    if (user) {
      setEditingUser(user);
      
      // Fetch user's institution assignments
      let userInstitutionIds: number[] = [];
      try {
        const response = await axios.get(`${API_BASE_URL}/api/admin/users/${user.id}/institutions`);
        userInstitutionIds = response.data.institutions?.map((i: any) => i.id) || [];
      } catch (error) {
        console.error('Error fetching user institutions:', error);
      }
      
      setFormData({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        user_role: user.user_role || user.user_type,
        organization_id: user.organization_id || null,
        institution_ids: userInstitutionIds,
        is_active: user.is_active
      });
    } else {
      setEditingUser(null);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        user_role: 'teacher',
        organization_id: organization?.id || null,
        institution_ids: [],
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      user_role: 'teacher',
      organization_id: organization?.id || null,
      institution_ids: [],
      is_active: true
    });
  };

  const handleInputChange = (field: keyof UserFormData) => (event: any) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    
    // If organization changes, clear institution selections
    if (field === 'organization_id') {
      setFormData({ ...formData, [field]: value, institution_ids: [] });
    } else if (field === 'user_role') {
      // If user becomes global admin, clear org/institution assignments (optional, can be re-added)
      if (value === 'global_admin') {
        setFormData({ ...formData, user_role: value, organization_id: null, institution_ids: [] });
      } else {
        // If switching away from global admin, keep existing org if present, otherwise default to current org if available
        const defaultOrgId = formData.organization_id ?? organization?.id ?? null;
        setFormData({ ...formData, user_role: value, organization_id: defaultOrgId });
      }
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleSubmit = async () => {
    try {
      // Validate that non-global admins are assigning users to their own organization
      if (!isGlobalAdmin && formData.user_role !== 'global_admin') {
        if (!formData.organization_id) {
          showSnackbar('Organization is required', 'error');
          return;
        }
        if (organization && formData.organization_id !== organization.id) {
          showSnackbar('You can only assign users to your own organization', 'error');
          return;
        }
      }
      
      if (editingUser) {
        // Update existing user
        await axios.put(`${API_BASE_URL}/api/admin/users/${editingUser.id}`, formData);
        showSnackbar('User updated successfully', 'success');
      } else {
        // Create new user
        await axios.post(`${API_BASE_URL}/api/admin/users`, formData);
        showSnackbar('User created successfully', 'success');
      }
      handleCloseDialog();
      fetchUsers();
    } catch (error: any) {
      console.error('Error saving user:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save user';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDeleteClick = (user: User) => {
    setDeleteDialog({ open: true, user });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.user) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/admin/users/${deleteDialog.user.id}`);
      showSnackbar('User deleted successfully', 'success');
      setDeleteDialog({ open: false, user: null });
      fetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete user';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, user: null });
  };

  const [impersonateDialog, setImpersonateDialog] = useState({ open: false, user: null as User | null });

  const handleImpersonate = (user: User) => {
    setImpersonateDialog({ open: true, user });
  };

  const handleImpersonateConfirm = async () => {
    if (!impersonateDialog.user) return;
    try {
      await impersonateUser(impersonateDialog.user.id);
    } catch (error: any) {
      showSnackbar(error.message || 'Failed to impersonate user', 'error');
    }
    setImpersonateDialog({ open: false, user: null });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusChip = (user: User) => {
    if (!user.is_active) {
      return <Chip label="Inactive" color="error" size="small" />;
    }
    return <Chip label="Active" color="success" size="small" />;
  };

  const getUserInitials = (user: User): string => {
    const firstInitial = user.first_name?.charAt(0).toUpperCase() || '';
    const lastInitial = user.last_name?.charAt(0).toUpperCase() || '';
    return `${firstInitial}${lastInitial}`;
  };

  const getUserAvatarColor = (userId: number): string => {
    const colors = [
      '#1976d2', // blue
      '#388e3c', // green
      '#d32f2f', // red
      '#f57c00', // orange
      '#7b1fa2', // purple
      '#0097a7', // cyan
      '#c2185b', // pink
      '#5d4037', // brown
      '#455a64', // blue grey
      '#e64a19', // deep orange
    ];
    return colors[userId % colors.length];
  };

  const getAvailableRoles = (): Array<{value: string, label: string}> => {
    // Global admins can assign any role
    if (isGlobalAdmin) {
      return [
        { value: 'teacher', label: 'Teacher' },
        { value: 'institution_admin', label: 'Institution Admin' },
        { value: 'org_admin', label: 'Organization Admin' },
        { value: 'global_admin', label: 'Global Admin' }
      ];
    }
    // Org admins can assign up to org_admin
    if (isOrgAdmin) {
      return [
        { value: 'teacher', label: 'Teacher' },
        { value: 'institution_admin', label: 'Institution Admin' },
        { value: 'org_admin', label: 'Organization Admin' }
      ];
    }
    // Institution admins can only assign teacher
    if (isInstitutionAdmin) {
      return [
        { value: 'teacher', label: 'Teacher' },
        { value: 'institution_admin', label: 'Institution Admin' }
      ];
    }
    // Default to teacher only
    return [
      { value: 'teacher', label: 'Teacher' }
    ];
  };

  const getUserTypeChip = (user: User) => {
    const role = user.user_role || user.user_type;
    const roleLabels: Record<string, string> = {
      'global_admin': 'Global Admin',
      'org_admin': 'Org Admin',
      'institution_admin': 'Institution Admin',
      'teacher': 'Teacher',
      'admin': 'Admin'
    };
    
    const roleColors: Record<string, 'primary' | 'secondary' | 'success' | 'default'> = {
      'global_admin': 'primary',
      'org_admin': 'secondary',
      'institution_admin': 'success',
      'teacher': 'default',
      'admin': 'primary'
    };
    
    return (
      <Chip 
        label={roleLabels[role] || role.charAt(0).toUpperCase() + role.slice(1)} 
        color={roleColors[role] || 'default'} 
        size="small" 
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="app-container">
      <Sidebar />
      <Box className="main-content">
        <Header />
        <Box className="user-management">
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1">
                  User Management
                </Typography>
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchUsers}
                    sx={{ mr: 2 }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                  >
                    Add User
                  </Button>
                </Box>
              </Box>

          {/* Filters */}
          <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Filters
            </Typography>
            <Grid container spacing={2} alignItems="center">
              {/* Organization Filter - Super Admins only */}
              {isGlobalAdmin && (
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Organization</InputLabel>
                    <Select
                      value={filters.organizationId}
                      onChange={(e) => setFilters({ ...filters, organizationId: e.target.value })}
                      label="Organization"
                    >
                      <MenuItem value="">All Organizations</MenuItem>
                      {organizations.map((org) => (
                        <MenuItem key={org.id} value={org.id.toString()}>
                          {org.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Institution Filter - Super Admins and Org Admins */}
              {(isGlobalAdmin || isOrgAdmin) && (
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Institution</InputLabel>
                    <Select
                      value={filters.institutionId}
                      onChange={(e) => setFilters({ ...filters, institutionId: e.target.value })}
                      label="Institution"
                    >
                      <MenuItem value="">All Institutions</MenuItem>
                      {institutions.map((inst) => (
                        <MenuItem key={inst.id} value={inst.id.toString()}>
                          {inst.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* User Role Filter - Super Admins and Org Admins */}
              {(isGlobalAdmin || isOrgAdmin) && (
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={filters.userRole}
                      onChange={(e) => setFilters({ ...filters, userRole: e.target.value })}
                      label="Role"
                    >
                      <MenuItem value="">All Roles</MenuItem>
                      <MenuItem value="global_admin">Global Admin</MenuItem>
                      <MenuItem value="org_admin">Org Admin</MenuItem>
                      <MenuItem value="institution_admin">Institution Admin</MenuItem>
                      <MenuItem value="teacher">Teacher</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Search Filter - All users */}
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Search"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Name or email..."
                />
              </Grid>

              {/* Status Filter - All users */}
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="all">All</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Clear Filters Button */}
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  variant="outlined"
                  onClick={() => setFilters({ 
                    organizationId: '', 
                    institutionId: '', 
                    userRole: '', 
                    search: '', 
                    status: 'active' 
                  })}
                  fullWidth
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </Box>

          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {users.map((user) => (
                <Paper key={user.id} sx={{ p: 2, borderRadius: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                      <Box
                        sx={{
                          width: 40, height: 40, minWidth: 40, borderRadius: '50%',
                          backgroundColor: getUserAvatarColor(user.id), color: 'white',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '13px', fontWeight: 'bold', flexShrink: 0
                        }}
                      >
                        {getUserInitials(user)}
                      </Box>
                      <Box minWidth={0}>
                        <Typography variant="subtitle2" noWrap>{user.full_name}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{user.email}</Typography>
                      </Box>
                    </Box>
                    {getStatusChip(user)}
                  </Box>
                  <Box display="flex" gap={1} flexWrap="wrap" mb={1.5}>
                    {getUserTypeChip(user)}
                    {user.organization_name && <Chip label={user.organization_name} size="small" variant="outlined" />}
                  </Box>
                  <Box display="flex" justifyContent="flex-end" gap={0.5}>
                    {isGlobalAdmin && user.id !== currentUser?.id && (
                      <IconButton onClick={() => handleImpersonate(user)} color="warning" size="small" aria-label="View as this user">
                        <VisibilityIcon />
                      </IconButton>
                    )}
                    <IconButton onClick={() => handleOpenDialog(user)} color="primary" size="small" aria-label="Edit user">
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDeleteClick(user)} color="error" size="small" aria-label="Delete user">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Organization</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Box
                            sx={{
                              width: 32, height: 32, minWidth: 32, minHeight: 32,
                              borderRadius: '50%', backgroundColor: getUserAvatarColor(user.id),
                              color: 'white', display: 'flex', alignItems: 'center',
                              justifyContent: 'center', marginRight: '12px',
                              fontSize: '12px', fontWeight: 'bold', flexShrink: 0
                            }}
                          >
                            {getUserInitials(user)}
                          </Box>
                          {user.full_name}
                        </Box>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{getUserTypeChip(user)}</TableCell>
                      <TableCell>{user.organization_name || 'N/A'}</TableCell>
                      <TableCell>{getStatusChip(user)}</TableCell>
                      <TableCell>{user.last_login ? formatDate(user.last_login) : 'Never'}</TableCell>
                      <TableCell>{formatDate(user.created_at)}</TableCell>
                      <TableCell align="center">
                        {isGlobalAdmin && user.id !== currentUser?.id && (
                          <IconButton onClick={() => handleImpersonate(user)} color="warning" size="small" aria-label="View as this user">
                            <VisibilityIcon />
                          </IconButton>
                        )}
                        <IconButton onClick={() => handleOpenDialog(user)} color="primary" size="small" aria-label="Edit user">
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleDeleteClick(user)} color="error" size="small" aria-label="Delete user">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Add/Edit User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={handleInputChange('first_name')}
                required
                inputProps={{ 'aria-label': 'First name' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={handleInputChange('last_name')}
                required
                inputProps={{ 'aria-label': 'Last name' }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                required
                inputProps={{ 'aria-label': 'Email address' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>User Role</InputLabel>
                <Select
                  value={formData.user_role}
                  onChange={handleInputChange('user_role')}
                  label="User Role"
                  inputProps={{ 'aria-label': 'User role' }}
                >
                  {getAvailableRoles().map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.is_active ? 'active' : 'inactive'}
                  onChange={(e) => handleInputChange('is_active')({ target: { checked: e.target.value === 'active' } })}
                  label="Status"
                  inputProps={{ 'aria-label': 'User status' }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Organization Selection (Global Admin only) */}
            {isGlobalAdmin && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('userManagement.form.organization')}</InputLabel>
                  <Select
                    value={formData.organization_id || ''}
                    onChange={handleInputChange('organization_id')}
                    label={t('userManagement.form.organization')}
                    required={formData.user_role !== 'global_admin'}
                    inputProps={{ 'aria-label': 'Organization' }}
                  >
                    {formData.user_role === 'global_admin' && (
                      <MenuItem value="">{t('userManagement.form.organizationNone')}</MenuItem>
                    )}
                    {organizations.map((org) => (
                      <MenuItem key={org.id} value={org.id}>
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {/* Institution Selection (Admins can manage institution memberships) */}
            {(isGlobalAdmin || isOrgAdmin) && (
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={institutions.filter(inst => 
                    formData.organization_id ? inst.organization_id === formData.organization_id : true
                  )}
                  value={institutions.filter(i => formData.institution_ids.includes(i.id))}
                  onChange={(event, newValue) => {
                    setFormData({ ...formData, institution_ids: newValue.map(i => i.id) });
                  }}
                  getOptionLabel={(option) => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('userManagement.form.institutions')}
                      placeholder={formData.organization_id ? t('userManagement.form.selectInstitutions') : t('userManagement.form.selectOrganizationFirst')}
                      disabled={!formData.organization_id}
                      inputProps={{
                        ...params.inputProps,
                        'aria-label': 'Institutions'
                      }}
                    />
                  )}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox checked={selected} style={{ marginRight: 8 }} />
                      {option.name}
                    </li>
                  )}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{deleteDialog.user?.full_name}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Impersonation Confirmation */}
      <ConfirmationModal
        isOpen={impersonateDialog.open}
        title="View As User"
        message={`View the application as ${impersonateDialog.user?.full_name}? You will see exactly what this user sees, with their permissions and data access.`}
        confirmText="View As User"
        cancelText="Cancel"
        variant="warning"
        onConfirm={handleImpersonateConfirm}
        onCancel={() => setImpersonateDialog({ open: false, user: null })}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;
